import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "bb_session_token";

export function setAuthToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export const http = axios.create({
  baseURL: API,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach Bearer token on every request as fallback for environments where
// cross-site cookies are blocked (mobile Safari / PWA / in-app browsers).
http.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export async function fetchRegions() {
  const { data } = await http.get("/regions");
  return data;
}

export async function fetchAllSnapshots() {
  const { data } = await http.get("/snapshots");
  return data;
}

export async function fetchSnapshot(regionId) {
  const { data } = await http.get(`/snapshot/${regionId}`);
  return data;
}

export async function triggerPipeline() {
  const { data } = await http.post("/pipeline/run");
  return data;
}

export async function fetchPipelineRuns() {
  const { data } = await http.get("/pipeline/runs");
  return data;
}

export async function generateCaption(regionId, platform = "instagram") {
  const { data } = await http.post("/caption", { region_id: regionId, platform });
  return data;
}

export async function fetchPublicInsights() {
  const { data } = await http.get("/public/insights");
  return data;
}

export async function fetchPublicInsight(slug) {
  const { data } = await http.get(`/public/insights/${slug}`);
  return data;
}

export async function generateInsight() {
  const { data } = await http.post("/insights/generate");
  return data;
}

export async function fetchRecommendations(regionId) {
  const { data } = await http.get(`/public/recommendations/${regionId}`);
  return data;
}

export async function fetchLandingSpotlight() {
  const { data } = await http.get(`/landing/spotlight`);
  return data;
}

// --- Daily content (Heute / Content Studio) ---
export async function fetchDailyToday() {
  const { data } = await http.get(`/daily/today`);
  return data;
}
export async function fetchDailyByDate(date) {
  const { data } = await http.get(`/daily/by-date/${date}`);
  return data;
}
export async function fetchDailyDates() {
  const { data } = await http.get(`/daily/dates`);
  return data.dates || [];
}
export async function adminListDailyPosts(status) {
  const { data } = await http.get(`/admin/daily`, { params: status ? { status } : {} });
  return data.posts || [];
}
export async function adminGenerateDaily(count = 4) {
  const { data } = await http.post(`/admin/daily/generate`, { count });
  return data;
}
export async function adminGenerateCustomDaily(topic, fmt = "long", autoPublish = false) {
  const { data } = await http.post(`/admin/daily/generate-custom`, {
    topic, fmt, auto_publish: autoPublish,
  });
  return data;
}
export async function adminGenerateBulkDaily(topics, fmt = "instagram", autoPublish = true) {
  const { data } = await http.post(`/admin/daily/generate-bulk`, {
    topics, fmt, auto_publish: autoPublish,
  });
  return data;
}
export async function adminEditDailyPost(id, fields) {
  const { data } = await http.patch(`/admin/daily/${id}`, fields);
  return data;
}
export async function adminPublishDailyPost(id) {
  const { data } = await http.post(`/admin/daily/${id}/publish`);
  return data;
}
export async function adminArchiveDailyPost(id) {
  const { data } = await http.post(`/admin/daily/${id}/archive`);
  return data;
}
export async function adminDeleteDailyPost(id) {
  const { data } = await http.delete(`/admin/daily/${id}`);
  return data;
}
export async function adminRegenerateDailyImage(id) {
  const { data } = await http.post(`/admin/daily/${id}/regenerate-image`);
  return data;
}
export function adminExportDailyUrl(id, format) {
  return `${API}/admin/daily/${id}/export?format=${format}`;
}
export function adminStoryUrl(id) {
  return `${API}/admin/daily/${id}/story.png`;
}
export function adminReelUrl(id, { voice = true, music = true } = {}) {
  return `${API}/admin/daily/${id}/reel.mp4?voice=${voice ? 1 : 0}&music=${music ? 1 : 0}`;
}

export async function authMe() {
  const { data } = await http.get("/auth/me");
  return data;
}

export async function authExchange(sessionId) {
  const { data } = await http.post("/auth/session", { session_id: sessionId });
  if (data?.session_token) setAuthToken(data.session_token);
  return data;
}

export async function authLogout() {
  try {
    const { data } = await http.post("/auth/logout");
    return data;
  } finally {
    setAuthToken(null);
  }
}

export async function authDemo() {
  const { data } = await http.post("/auth/demo");
  if (data?.session_token) setAuthToken(data.session_token);
  return data;
}

export async function billingPackages() {
  const { data } = await http.get("/billing/packages");
  return data;
}

export async function billingCheckout(packageId = "pro_month") {
  const { data } = await http.post("/billing/checkout", {
    package_id: packageId,
    origin_url: window.location.origin,
  });
  return data;
}

export async function billingStatus(sessionId) {
  const { data } = await http.get(`/billing/status/${sessionId}`);
  return data;
}

export async function subscribeEmail(email) {
  const { data } = await http.post("/subscribe", { email });
  return data;
}

export async function fetchForecast(regionId) {
  const { data } = await http.get(`/forecast/${regionId}`);
  return data;
}

export async function fetchForecastAll() {
  const { data } = await http.get(`/forecast`);
  return data;
}

export async function listAlerts() {
  const { data } = await http.get(`/alerts`);
  return data;
}

export async function createAlert(payload) {
  const { data } = await http.post(`/alerts`, payload);
  return data;
}

export async function deleteAlert(alertId) {
  const { data } = await http.delete(`/alerts/${alertId}`);
  return data;
}

export async function listShares() {
  const { data } = await http.get(`/share`);
  return data;
}

export async function createShare(regionId) {
  const { data } = await http.post(`/share`, { region_id: regionId });
  return data;
}

export async function deleteShare(shareId) {
  const { data } = await http.delete(`/share/${shareId}`);
  return data;
}

export async function listMyFields() {
  const { data } = await http.get(`/myfields`);
  return data;
}

export async function createMyField(payload) {
  const { data } = await http.post(`/myfields`, payload);
  return data;
}

export async function deleteMyField(id) {
  const { data } = await http.delete(`/myfields/${id}`);
  return data;
}

export async function fetchMyFieldSnapshot(id, force = false) {
  const { data } = await http.get(`/myfields/${id}/snapshot`, { params: { force } });
  return data;
}


export async function fetchPublicShare(token) {
  const { data } = await http.get(`/public/share/${token}`);
  return data;
}

// --- Commodities ---
export async function fetchCommodities() {
  const { data } = await http.get(`/commodities`);
  return data;
}

export async function fetchCommodityDetail(id, { days = 180, horizon = 30 } = {}) {
  const { data } = await http.get(`/commodities/${id}`, { params: { days, horizon } });
  return data;
}

export async function fetchCommoditiesContextDeAt() {
  const { data } = await http.get(`/commodities/context/de-at`);
  return data;
}

export async function fetchMarketAcademy() {
  const { data } = await http.get(`/market-academy/lessons`);
  return data;
}

// --- Grassland / Weide-Analyse ---
export async function fetchGrassland(regionId) {
  const { data } = await http.get(`/grassland/${regionId}`);
  return data;
}

export async function fetchGrasslandAll() {
  const { data } = await http.get(`/grassland`);
  return data;
}

export async function fetchMyFieldGrassland(fieldId, force = false) {
  const { data } = await http.get(`/myfields/${fieldId}/grassland`, { params: { force } });
  return data;
}

export async function subscribeFieldAlert(fieldId, { email, push_subscription }) {
  const body = {};
  if (email) body.email = email;
  if (push_subscription) body.push_subscription = push_subscription;
  const { data } = await http.post(`/myfields/${fieldId}/alerts/subscribe`, body);
  return data;
}

// --- Academy ---
export async function fetchAcademyCategories() {
  const { data } = await http.get(`/academy`);
  return data;
}

export async function addAcademyVideo(catId, { url, title, creator }) {
  const { data } = await http.post(`/academy/${catId}/videos`, { url, title, creator });
  return data;
}

export async function removeAcademyVideo(catId, videoId) {
  const { data } = await http.delete(`/academy/${catId}/videos/${videoId}`);
  return data;
}

export async function fetchPublicGrassland(regionId) {
  const { data } = await http.get(`/public/grassland/${regionId}`);
  return data;
}

// --- Agronomy (Saattermin + Krankheits-Frühwarner) ---
export async function fetchAgronomyCrops() {
  const { data } = await http.get(`/agronomy/crops`);
  return data;
}

export async function fetchSowingPlan(regionId, crop) {
  const { data } = await http.get(`/agronomy/sowing/${regionId}`, { params: { crop } });
  return data;
}

export async function fetchDiseaseRisk(regionId) {
  const { data } = await http.get(`/agronomy/diseases/${regionId}`);
  return data;
}

// --- Podcast ---
export async function listPodcastEpisodes() {
  const { data } = await http.get(`/podcast/episodes`);
  return data.episodes || [];
}
export async function createPodcastEpisode(payload) {
  const { data } = await http.post(`/podcast/episodes`, payload);
  return data;
}
export async function deletePodcastEpisode(id) {
  const { data } = await http.delete(`/podcast/episodes/${id}`);
  return data;
}
export async function pinPodcastEpisode(id) {
  const { data } = await http.patch(`/podcast/episodes/${id}/pin`);
  return data;
}
export async function generatePodcastScript() {
  const { data } = await http.post(`/podcast/script/generate`);
  return data;
}

// --- Marketplace ---
export async function fetchMarketCategories() {
  const { data } = await http.get(`/marketplace/categories`);
  return data;
}
export async function searchMarketListings({ lat, lon, radius_km = 10, kind, category } = {}) {
  const params = {};
  if (lat != null) params.lat = lat;
  if (lon != null) params.lon = lon;
  if (radius_km) params.radius_km = radius_km;
  if (kind) params.kind = kind;
  if (category) params.category = category;
  const { data } = await http.get(`/marketplace/listings`, { params });
  return data;
}
export async function createMarketListing(payload) {
  const { data } = await http.post(`/marketplace/listings`, payload);
  return data;
}
export async function deleteMarketListing(id) {
  const { data } = await http.delete(`/marketplace/listings/${id}`);
  return data;
}
export async function contactMarketLister(payload) {
  const { data } = await http.post(`/marketplace/contact`, payload);
  return data;
}

// --- Voice Journal ---
export async function transcribeAudio(blob, language = "de") {
  const fd = new FormData();
  fd.append("audio", blob, "memo.webm");
  fd.append("language", language);
  const { data } = await http.post(`/journal/transcribe`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return data;
}
export async function createJournalEntry(payload) {
  const { data } = await http.post(`/journal/entries`, payload);
  return data;
}
export async function listJournalEntries(fieldId) {
  const params = fieldId ? { field_id: fieldId } : {};
  const { data } = await http.get(`/journal/entries`, { params });
  return data.entries || [];
}
export async function deleteJournalEntry(id) {
  const { data } = await http.delete(`/journal/entries/${id}`);
  return data;
}

// --- Geo: Landkreise/Bezirke hierarchy + PLZ search + on-demand snapshot ---
export async function fetchGeoHierarchy() {
  const { data } = await http.get(`/geo/hierarchy`);
  return data;
}

export async function searchPlz(q, country) {
  const params = { q };
  if (country) params.country = country;
  const { data } = await http.get(`/geo/plz`, { params });
  return data.results || [];
}

export async function fetchLocationSnapshot(locId, force = false) {
  const { data } = await http.get(`/geo/location/${locId}/snapshot`, { params: { force } });
  return data;
}

// --- Phase 4: Impact · Heritage · Bees · Leaderboard ---
export async function fetchImpactLocation(locId, areaHa = 10) {
  const { data } = await http.get(`/impact/location/${locId}`, { params: { area_ha: areaHa } });
  return data;
}
export async function fetchImpactField(fieldId) {
  const { data } = await http.get(`/impact/field/${fieldId}`);
  return data;
}
export async function fetchHeritageRegion(regionId, window = 60) {
  const { data } = await http.get(`/heritage/region/${regionId}`, { params: { window } });
  return data;
}
export async function fetchHeritageLocation(locId, window = 60) {
  const { data } = await http.get(`/heritage/location/${locId}`, { params: { window } });
  return data;
}
export async function fetchBees(regionId) {
  const { data } = await http.get(`/bees/${regionId}`);
  return data;
}
export async function fetchLeaderboard(country) {
  const { data } = await http.get(`/leaderboard`, { params: country ? { country } : {} });
  return data;
}

// --- Schulmodus ---
export async function createSchoolInvite(payload) {
  const { data } = await http.post(`/school/invites`, payload);
  return data;
}
export async function listSchoolInvites() {
  const { data } = await http.get(`/school/invites`);
  return data.invites || [];
}
export async function revokeSchoolInvite(id) {
  const { data } = await http.delete(`/school/invites/${id}`);
  return data;
}
export async function schoolRedeem(code, nickname) {
  const { data } = await http.post(`/school/redeem-session`, { code, nickname });
  if (data?.session_token) setAuthToken(data.session_token);
  return data;
}

// --- Schulgarten (single polygon for is_school users) ---
export async function saveSchoolGarden(payload) {
  const { data } = await http.post(`/school-garden`, payload);
  return data;
}
export async function fetchMySchoolGarden() {
  const { data } = await http.get(`/school-garden/me`);
  return data.garden || null;
}
export async function deleteSchoolGarden() {
  const { data } = await http.delete(`/school-garden`);
  return data;
}
export async function fetchSchoolGardenSnapshot(force = false) {
  const { data } = await http.get(`/school-garden/snapshot`, { params: force ? { force: true } : {} });
  return data;
}
export async function fetchPublicSchoolGardens() {
  const { data } = await http.get(`/school-gardens/public`);
  return data;
}

// --- Schulgarten · Foto-Tagebuch ---
export async function uploadSchoolGardenPhoto(file, { caption = "", photo_date = null } = {}) {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("caption", caption);
  if (photo_date) form.append("photo_date", photo_date);
  const { data } = await http.post(`/school-garden/photos`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function fetchMySchoolGardenPhotos() {
  const { data } = await http.get(`/school-garden/photos`);
  return data;
}
export async function deleteSchoolGardenPhoto(photoId) {
  const { data } = await http.delete(`/school-garden/photos/${photoId}`);
  return data;
}
export async function fetchPublicSchoolGardenPhotos(limit = 12) {
  const { data } = await http.get(`/school-gardens/public/photos`, { params: { limit } });
  return data;
}
export function schoolGardenPhotoUrl(photoId) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/school-garden/photos/${photoId}.jpg`;
}

export async function fetchGenerationsStory(anchorId) {
  const { data } = await http.get(`/heritage/generations-story/${anchorId}`);
  return data;
}


